<template>
  <div class="ds-page tfa-page-wrapper">
    <div v-if="!is_head_verified" class="tfa-card-clean">
      <div class="tfa-center-text">
        <div class="tfa-icon-shield">
          <font-awesome-icon icon="lock" />
        </div>
        <h2 class="tfa-main-heading">Activate Two-Factor Authentication</h2>
        <p class="tfa-sub-heading">To secure your account, please complete the setup below.</p>
      </div>

      <div class="step-item">
        <div class="step-number">1</div>
        <div class="step-content text-left">
          Download <strong>Google Authenticator</strong> from the
          <a :href="playstore" target="_blank" class="store-link">Google Play Store</a>
          or
          <a :href="appstore" target="_blank" class="store-link">App Store</a>.
        </div>
      </div>
      <div v-if="!is_head_verified" class="step-item">
        <div class="step-number">2</div>
        <div class="step-content text-left">
          Scan this QR code with your authenticator app.
          <div class="qr-wrapper text-center">
            <img :src="qrcode" alt="2FA QR Code" class="qr-image text-center" />
          </div>
        </div>
      </div>
      <div class="step-item">
        <div class="step-number">3</div>
        <div class="step-content text-left">
          Enter the 6-digit verification code generated by your app.

          <div class="tfa-otp-row text-center">
            <input
              v-for="(digit, index) in 6"
              :key="index"
              :ref="'otp' + index"
              v-model="otpArray[index]"
              type="text"
              maxlength="1"
              pattern="[0-9]*"
              inputmode="numeric"
              class="tfa-otp-box text-center"
              placeholder="·"
              @input="handleOtpInput($event, index)"
              @keydown.delete="handleOtpDelete($event, index)"
            />
          </div>
        </div>
      </div>

      <div class="tfa-action-block">
        <el-button
          @click="onSubmit"
          :loading="loading.submit"
          class="tfa-submit-btn"
          type="primary"
        >
          Verify and Continue
        </el-button>
      </div>
    </div>

    <div v-else class="ds-page">
      <div class="ds-page profile-table-container silinder">
        <h3 class="table-title">User Profile: {{ role }}</h3>

        <table class="profile-summary-table">
          <thead>
            <tr>
              <th colspan="2">Consolidated User & System Information</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="field-label">Account Status</td>
              <td>
                <span class="badge badge-active"> {{ role_data?.status }}</span>
              </td>
            </tr>
            <tr>
              <td class="field-label">User Information</td>
              <td>
                <strong>Name:</strong> {{ role_data?.name }} <br />
                <strong>Email:</strong> {{ role_data?.email }}
              </td>
            </tr>
            <tr>
              <td class="field-label">Account Activity</td>
              <td>
                <strong>Created:</strong> {{ role_data?.created_at }}<br />
                <strong>Last Login:</strong> {{ role_data?.last_login_at }}
              </td>
            </tr>

            <tr>
              <td class="field-label">Two-Factor Auth (2FA)</td>
              <td>
                <span class="badge badge-tfa"> {{ role_data?.tfa_active }}</span>
              </td>
            </tr>
            <tr>
              <td class="field-label">System Permissions</td>
              <td>
                <div class="permission-group flex flex-wrap gap-4">
                  <span class="perm-item inline-flex items-center gap-1">
                    Is Head Role:
                    <span class="badge badge-inactive">{{ role_data?.is_head_role }}</span>
                  </span>

                  <span class="perm-item inline-flex items-center gap-1">
                    Is Checker:
                    <span class="badge badge-active">{{ role_data?.is_checker }}</span>
                  </span>

                  <span class="perm-item inline-flex items-center gap-1">
                    Is Maker:
                    <span class="badge badge-active">{{ role_data?.is_maker }}</span>
                  </span>
                </div>
              </td>
            </tr>

            <tr>
              <td class="field-label">Assigned Role</td>
              <td>
                <strong>Super Admin</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import { INIT_2FA_HEAD, ACTIVATE_2FA_HEAD, INQ_HEAD, MY_PROFILE } from '@/store/modules/auth';
import popupErrorMessages from '@/library/popup-error-messages';

export default {
  name: 'Head',
  data() {
    return {
      playstore:
        'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en&gl=US',
      appstore: 'https://apps.apple.com/us/app/google-authenticator/id388497605',
      form: {
        code: '',
      },
      role: '',
      role_data: '',
      qrcode: '',
      loader: null,
      loaderStack: 0,
      loading: {
        submit: false,
      },

      need_tfa: false,
      is_headrole: false,
      is_head_verified: false,
      is_head: false,
      is_otp_secret: false,
      is_active: false,
      otpArray: ['', '', '', '', '', ''],
      loading: { submit: false },
      isSendingOtp: false,
    };
  },
  methods: {
    showLoader() {
      if (this.loaderStack === 0) {
        this.loader = this.$loading.show();
      }
      this.loaderStack += 1;
    },
    hideLoader() {
      this.loaderStack -= 1;
      if (this.loaderStack === 0) {
        this.loader.hide();
      }
    },
    async loadProfile() {
      try {
        const response = await this.$store.dispatch(MY_PROFILE);
        this.role =
          response?.me?.role?.name ||
          response?.data?.me?.role?.name ||
          response?.data?.data?.me?.role?.name;

        this.role_data = response?.me || response?.data?.me || response?.data?.data?.me;
        this.need_tfa = this.myProfile?.tfa_active === false;
        if (this.need_tfa) {
          await this.isHeadVerified();
        }
      } catch (error) {
        console.error('Error loading profile data:', error);
      }
    },
    handleOtpInput(event, index) {
      const value = event.target.value;
      if (value && index < 5) {
        this.$refs['otp' + (index + 1)][0].focus();
      }
    },
    handleOtpDelete(event, index) {
      if (!event.target.value && index > 0) {
        this.$refs['otp' + (index - 1)][0].focus();
      }
    },
    async isHeadVerified() {
      this.showLoader();
      try {
        const response = await this.$store.dispatch(INQ_HEAD);
        this.is_head_verified = response.is_active;
        // if (!this.is_head_verified && this.is_headrole == true) {
        if (!this.is_head_verified) {
          this.generateQr();
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        this.hideLoader();
      }
    },
    async generateQr() {
      this.showLoader();
      const response = await this.$store.dispatch(INIT_2FA_HEAD);
      this.qrcode = response.otp_auth_url;
      this.hideLoader();
    },
    async onSubmit() {
      const codeString = String(this.combinedCode || '').trim();
      if (codeString.length < 6) {
        this.$message.error('Please enter a valid 6-digit code');
        return;
      }
      this.showLoader();
      this.loading.submit = true;
      try {
        const response = await this.$store.dispatch(ACTIVATE_2FA_HEAD, { code: codeString });
        if (response.is_valid === true) {
          this.is_active = true;
          this.isHeadVerified();
          // this.loadSettingPolicy();
          this.$message({
            title: 'Success',
            type: 'success',
            message: 'Successfully activate two-factor authentication',
          });
          this.$router.push('/companies').catch();
        } else {
          this.is_active = true;
          this.$message.error('Invalid 2FA code. Please try again.');
        }
      } catch (err) {
        console.error('2FA Error:', err);
        this.is_active = false;
        popupErrorMessages(err?.response?.data).catch(() => {});
      } finally {
        this.loading.submit = false;
        this.hideLoader();
      }
    },

    handlerInput(evt) {
      const theEvent = evt || window.event;
      let key = '';
      if ([8, 91].includes(theEvent.keyCode) || theEvent.ctrlKey || theEvent.metaKey) {
        return;
      }
      if (theEvent.type === 'paste') {
        key = theEvent.clipboardData.getData('text/plain');
      } else {
        key = theEvent.keyCode || theEvent.which;
        key = String.fromCharCode(key);
      }
      const regex = /[0-9]/;
      if (!regex.test(key)) {
        theEvent.returnValue = false;
        if (theEvent.preventDefault) theEvent.preventDefault();
      }
    },
  },
  async mounted() {
    this.isHeadVerified();
    this.loadProfile();
  },

  computed: {
    combinedCode() {
      return this.otpArray.join('');
    },
  },
};
</script>

<style scoped>
/* Main Container styling */
.tfa-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  padding: 2rem 1rem;
  background-color: #f8fafc;
}

/* Card Styling */
.tfa-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 520px;
  padding: 2.5rem;
  box-sizing: border-box;
}

/* Header Styling */
.tfa-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.icon-badge {
  background-color: #fef2f2;
  color: #ef4444;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem auto;
  font-size: 24px;
}

.tfa-title {
  color: #1e293b;
  font-size: 1.35rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.tfa-subtitle {
  color: #64748b;
  font-size: 0.9rem;
  margin: 0;
}

.divider {
  border: 0;
  border-top: 1px solid #e2e8f0;
  margin: 1.5rem 0;
}

/* Step Layout Engine */
.tfa-body {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.step-item {
  display: flex;
  gap: 1rem;
}

.step-number {
  background-color: #f1f5f9;
  color: #475569;
  font-weight: 700;
  font-size: 0.85rem;
  min-width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}

.step-content {
  color: #334155;
  font-size: 0.925rem;
  line-height: 1.5;
  flex: 1;
}

.store-link {
  color: #f26522; /* Matches Venteny Orange accent color */
  text-decoration: none;
  font-weight: 600;
}

.store-link:hover {
  text-decoration: underline;
}

/* QR Code Container */
.qr-wrapper {
  margin-top: 0.75rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  display: inline-block;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  justify-content: center;
  display: flex;
  align-items: center;
  margin: 15px auto;
  width: 170px;
}

.qr-image {
  width: 140px;
  height: 140px;
  display: block;
}

/* Modern OTP Inputs Row */
.otp-inputs-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 1rem;
  max-width: 360px;
}

.otp-field {
  width: 100%;
  max-width: 48px;
  height: 52px;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  background-color: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  outline: none;
  transition: all 0.2s ease;
}

.otp-field:focus {
  border-color: #f26522; /* Venteny Brand Focal Color */
  background-color: #ffffff;
  box-shadow: 0 0 0 4px rgba(242, 101, 34, 0.15);
}

.otp-field::placeholder {
  color: #cbd5e1;
}

/* Verify Button styling */
.tfa-footer {
  margin-top: 2rem;
}

.verify-btn {
  width: 100%;
  height: 46px !important;
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  background-color: #f26522 !important; /* Venteny Orange */
  border-color: #f26522 !important;
  border-radius: 8px !important;
  transition: opacity 0.2s ease;
}

.verify-btn:hover {
  opacity: 0.9;
}

.verified-content {
  padding: 3rem;
  text-align: center;
  color: #475569;
}

.tfa-page-wrapper {
  display: block;
  width: 100%;
  padding: 40px 20px;
  box-sizing: border-box;
}

/* Stable Card Container - Explicit Widths to Prevent Overlaps */
.tfa-card-clean {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  max-width: 580px;
  margin: 0 auto;
  padding: 35px;
  box-sizing: border-box;
}

/* Header Elements */
.tfa-center-text {
  text-align: center;
  margin-bottom: 30px;
}

.tfa-icon-shield {
  background-color: #fef2f2;
  color: #ef4444;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px auto;
  font-size: 20px;
}

.tfa-main-heading {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.tfa-sub-heading {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* Strict Vertical Layout Step Blocks */
.tfa-step-block {
  display: block !important; /* Overrides any external conflicting styles */
  margin-bottom: 25px;
  clear: both;
}

.tfa-step-badge {
  display: block;
  width: fit-content;
  background-color: #f1f5f9;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 20px;
  margin-bottom: 8px;
}

.tfa-step-instruction {
  font-size: 14px;
  color: #334155;
  line-height: 1.5;
  margin: 0 0 10px 0;
}

.v-link {
  color: #f26522; /* Venteny Orange */
  text-decoration: none;
  font-weight: 600;
}
.v-link:hover {
  text-decoration: underline;
}

/* QR Center Box Setup */
.tfa-qr-container {
  display: block;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  width: 140px;
  margin: 12px auto;
}

.tfa-qr-img {
  width: 140px;
  height: 140px;
  display: block;
}

/* Bulletproof 6-Digit Row Layout */
.tfa-otp-row {
  display: grid !important;
  grid-template-columns: repeat(6, 1fr) !important;
  gap: 10px !important;
  max-width: 380px;
  margin: 15px 0 0 0;
}

.tfa-otp-box {
  width: 100% !important;
  height: 50px !important;
  text-align: center !important;
  font-size: 20px !important;
  font-weight: 700 !important;
  color: #1e293b !important;
  background-color: #f8fafc !important;
  border: 2px solid #e2e8f0 !important;
  border-radius: 8px !important;
  outline: none !important;
  box-sizing: border-box !important;
  transition: all 0.15s ease;
}

.tfa-otp-box:focus {
  border-color: #f26522 !important;
  background-color: #ffffff !important;
  box-shadow: 0 0 0 3px rgba(242, 101, 34, 0.15) !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Bottom Button Styling */
.tfa-action-block {
  margin-top: 30px;
  display: block;
}

.tfa-submit-btn {
  display: block !important;
  width: 100% !important;
  height: 46px !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  background-color: #f26522 !important; /* Venteny Orange */
  border-color: #f26522 !important;
  color: #ffffff !important;
  border-radius: 6px !important;
}

.tfa-submit-btn:hover {
  background-color: #f89615 !important;
  border-color: #d95316 !important;
}

.tfa-success-state {
  text-align: center;
  padding: 40px;
  color: #475569;
}

.profile-table-container {
  width: 100%;
  max-width: 900px;
  margin: 20px auto;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  padding: 24px;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.profile-summary-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 14px;
}

.profile-summary-table th {
  background-color: #f8f9fa;
  color: #495057;
  font-size: 15px;
  padding: 14px 16px;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
}

.profile-summary-table td {
  padding: 16px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
  color: #495057;
  line-height: 1.6;
}

.field-label {
  font-weight: 600;
  color: #212529;
  width: 25%;
  background-color: #fafafa;
}

/* Badges styling */
.badge {
  display: inline-block;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: bold;
  border-radius: 4px;
  text-transform: uppercase;
}

.badge-active {
  background-color: #e6f4ea;
  color: #137333;
}

.badge-inactive {
  background-color: #fce8e6;
  color: #c5221f;
}

.badge-tfa {
  background-color: #e8f0fe;
  color: #1a73e8;
  font-size: 11px;
}

.verification-tag {
  margin-left: 10px;
  color: #17a2b8;
  font-weight: 500;
}

.permission-group {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.perm-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

.permission-group {
  display: flex;
  flex-wrap: wrap;
  gap: 16px; /* Mengatur jarak antar item hak akses */
}

.perm-item {
  /* Kunci utama agar teks dan badge lurus sejajar di tengah */
  display: inline-flex;
  align-items: center;
  gap: 4px; /* Mengatur jarak horizontal antara teks dan badge */

  font-size: 14px;
  color: #4b5563;
}

.badge {
  /* Memastikan badge sendiri memiliki display yang ramah flexbox */
  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 2px 8px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 4px;
  line-height: 1; /* Memaksa tinggi baris mengecil mengikuti teks di dalam badge */
}

.badge-active {
  background-color: #def7ec;
  color: #03543f;
}

.badge-inactive {
  background-color: #fde8e8;
  color: #9b1c1c;
}
</style>
